/**
 * 邀请/佣金服务
 * 处理邀请、佣金、提现相关的业务逻辑
 */

import type { HttpClient } from '../http/HttpClient'
import type { IStorage } from '../storage/Storage'
import type {
  RewardDetail,
  RecommendUser,
  RecommendFailLog,
  RewardItem,
  WithdrawRecord,
  PaginatedResponse,
  InviteInfo,
  WithdrawParams,
  WithdrawResponse
} from '../../types/invite'
import { InviteAPI } from '../api/invite'
import { InviteStore } from '../stores/InviteStore'
import { BaseService } from './BaseService'

export class InviteService extends BaseService {
  private inviteAPI: InviteAPI
  private inviteStore: InviteStore

  constructor(httpClient: HttpClient, storage: IStorage) {
    super(httpClient, storage)
    this.inviteAPI = new InviteAPI(httpClient)
    this.inviteStore = new InviteStore(storage)
  }

  /**
   * 获取 InviteStore 实例
   */
  getInviteStore(): InviteStore {
    return this.inviteStore
  }

  /**
   * 获取佣金详情
   */
  async getRewardDetail(): Promise<RewardDetail> {
    try {
      const result = await this.inviteAPI.getRewardDetail()
      this.inviteStore.rewardDetail = result
      return result
    } catch (error) {
      console.error('[InviteService] 获取佣金详情失败', error)
      throw error
    }
  }

  /**
   * 申请提现
   */
  async applyWithdraw(params?: WithdrawParams): Promise<WithdrawResponse> {
    try {
      const result = await this.inviteAPI.applyWithdraw(params)

      // 提现成功后刷新佣金详情
      await this.getRewardDetail()

      return result
    } catch (error) {
      console.error('[InviteService] 申请提现失败', error)
      throw error
    }
  }

  /**
   * 获取佣金列表
   */
  async getRewardList(
    page: number = 1,
    perPage: number = 10
  ): Promise<PaginatedResponse<RewardItem>> {
    try {
      const result = await this.inviteAPI.getRewardList({
        page,
        per_page: perPage
      })

      // 更新 store
      if (page === 1) {
        this.inviteStore.rewardList = result.data
      } else {
        this.inviteStore.rewardList = [...this.inviteStore.rewardList, ...result.data]
      }

      return result
    } catch (error) {
      console.error('[InviteService] 获取佣金列表失败', error)
      throw error
    }
  }

  /**
   * 获取推荐失败列表
   */
  async getRecommendFailList(
    page: number = 1,
    perPage: number = 10
  ): Promise<PaginatedResponse<RecommendFailLog>> {
    try {
      return await this.inviteAPI.getRecommendFailList({
        page,
        per_page: perPage
      })
    } catch (error) {
      console.error('[InviteService] 获取推荐失败列表失败', error)
      throw error
    }
  }

  /**
   * 获取推荐用户列表
   */
  async getRecommendUserList(
    page: number = 1,
    perPage: number = 10
  ): Promise<PaginatedResponse<RecommendUser>> {
    try {
      const result = await this.inviteAPI.getRecommendUserList({
        page,
        per_page: perPage
      })

      // 更新 store
      if (page === 1) {
        this.inviteStore.recommendUsers = result.data
      } else {
        this.inviteStore.recommendUsers = [...this.inviteStore.recommendUsers, ...result.data]
      }

      return result
    } catch (error) {
      console.error('[InviteService] 获取推荐用户列表失败', error)
      throw error
    }
  }

  /**
   * 获取提现记录
   */
  async getWithdrawRecordList(
    page: number = 1,
    perPage: number = 10
  ): Promise<PaginatedResponse<WithdrawRecord>> {
    try {
      const result = await this.inviteAPI.getWithdrawRecordList({
        page,
        per_page: perPage
      })
      const data = result.data || []

      // 更新 store
      if (page === 1) {
        this.inviteStore.withdrawRecords = data
      } else {
        this.inviteStore.withdrawRecords = [...this.inviteStore.withdrawRecords, ...data]
      }

      return result
    } catch (error) {
      console.error('[InviteService] 获取提现记录失败', error)
      throw error
    }
  }

  /**
   * 获取邀请人信息
   */
  async getInviteInfo(): Promise<InviteInfo> {
    try {
      return await this.inviteAPI.getInviteInfo()
    } catch (error) {
      console.error('[InviteService] 获取邀请人信息失败', error)
      throw error
    }
  }

  // ========== 从 Store 获取数据的便捷方法 ==========

  /**
   * 获取当前佣金详情（从 store）
   */
  getCurrentRewardDetail(): RewardDetail | null {
    return this.inviteStore.rewardDetail
  }

  /**
   * 获取可用佣金
   */
  getAvailableReward(): number {
    return this.inviteStore.availableReward.value
  }

  /**
   * 获取冻结佣金
   */
  getFrozenReward(): number {
    return this.inviteStore.frozenReward.value
  }

  /**
   * 获取总佣金
   */
  getTotalReward(): number {
    return this.inviteStore.totalReward.value
  }

  /**
   * 是否可以提现
   */
  canWithdraw(): boolean {
    return this.inviteStore.canWithdraw.value
  }

  /**
   * 获取推荐用户数量
   */
  getRecommendUserCount(): number {
    return this.inviteStore.recommendUserCount.value
  }

  /**
   * 清除所有数据
   */
  clear(): void {
    this.inviteStore.clear()
  }
}
